import * as Dialog from "@radix-ui/react-dialog";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { Button } from "components/elements";
import { MultiUserSelector } from "components/form/MultiUserSelector";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { useExternalOrgConfirm } from "./useExternalOrgConfirm";

type Participant = {
    id: number;
    name: string;
    avatarUrl: string | null;
};

type ManageParticipantsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    discussionId: number;
    currentParticipants: Participant[];
};

export const ManageParticipantsModal = ({
    isOpen,
    onClose,
    discussionId,
    currentParticipants,
}: ManageParticipantsModalProps) => {
    const currentUser = useCurrentUser();
    const utils = trpc.useUtils();
    const { enqueueSnackbar } = useSnackbar();

    const [pendingRemovals, setPendingRemovals] = useState<Set<number>>(new Set());
    const [usersToAdd, setUsersToAdd] = useState<number[]>([]);

    const usersListQuery = trpc.users.list.useQuery({}, { enabled: isOpen });
    const usersList = usersListQuery.data?.users ?? [];

    const usersById = useMemo(() => new Map(usersList.map((u) => [u.id, u])), [usersList]);

    const currentParticipantIds = useMemo(
        () => new Set(currentParticipants.map((p) => p.id)),
        [currentParticipants],
    );

    // Existing participants' orgs (resolved from the user list) seed the
    // cross-org guard so we only warn when an addition introduces a *new*
    // 2nd+ external org (PRTL-1324).
    const existingParticipantUsers = useMemo(
        () =>
            currentParticipants
                .map((p) => usersById.get(p.id))
                .filter((u): u is (typeof usersList)[number] => Boolean(u)),
        [currentParticipants, usersById],
    );

    const { confirmAdditions, modal: externalOrgConfirmModal } = useExternalOrgConfirm({
        existingUsers: existingParticipantUsers,
    });

    const addCandidates = useMemo(
        () =>
            usersList.filter(
                (u) =>
                    u.id !== currentUser?.id &&
                    u.isPrivateEnabled &&
                    !currentParticipantIds.has(u.id),
            ),
        [usersList, currentUser?.id, currentParticipantIds],
    );

    const addMutation = trpc.discussions.addParticipants.useMutation();
    const removeMutation = trpc.discussions.removeParticipants.useMutation();

    const isPending = addMutation.isPending || removeMutation.isPending;

    const reset = () => {
        setPendingRemovals(new Set());
        setUsersToAdd([]);
    };

    const togglePendingRemoval = (userId: number) => {
        setPendingRemovals((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const handleSave = async () => {
        try {
            if (usersToAdd.length > 0) {
                const addUsers = usersToAdd
                    .map((id) => usersById.get(id))
                    .filter((u): u is (typeof usersList)[number] => Boolean(u));
                const confirmed = await confirmAdditions(addUsers);
                if (!confirmed) return;
                await addMutation.mutateAsync({ discussionId, userIds: usersToAdd });
            }
            if (pendingRemovals.size > 0) {
                await removeMutation.mutateAsync({
                    discussionId,
                    userIds: Array.from(pendingRemovals),
                });
            }
            await utils.discussions.getDiscussionView.invalidate({ discussionId });
            await utils.discussions.list.invalidate();
            reset();
            onClose();
        } catch (err) {
            Sentry.captureException(err, { tags: { feature: "manage-participants" } });
            const message =
                err instanceof Error && err.message ? err.message : "Failed to update recipients.";
            enqueueSnackbar(message, { variant: "error" });
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            reset();
            onClose();
        }
    };

    const hasChanges = usersToAdd.length > 0 || pendingRemovals.size > 0;

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
            <Dialog.Content
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="fixed left-1/2 top-1/2 z-50 max-h-[95vh] w-[95vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-lg"
            >
                <div className="flex items-center justify-between">
                    <div className="font-sans text-[24px] font-bold text-[#424242]">
                        Manage recipients
                    </div>
                    <Dialog.Close>
                        <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                    </Dialog.Close>
                </div>
                <hr className="-mx-[24px] my-4 border-[#c8c8c8]" />

                <div className="flex flex-col gap-4">
                    <div>
                        <div className="mb-2 font-semibold text-[#424242]">
                            Current participants
                        </div>
                        {currentParticipants.length === 0 ? (
                            <div className="text-sm text-gray-500">No active participants.</div>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {currentParticipants.map((p) => {
                                    const willBeRemoved = pendingRemovals.has(p.id);
                                    return (
                                        <li
                                            key={p.id}
                                            className={`flex items-center justify-between rounded border px-3 py-2 ${
                                                willBeRemoved
                                                    ? "border-red-200 bg-red-50"
                                                    : "border-gray-200"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {p.avatarUrl ? (
                                                    <img
                                                        src={p.avatarUrl}
                                                        alt={p.name}
                                                        className="h-7 w-7 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-300 text-xs font-semibold text-white">
                                                        {p.name?.[0]}
                                                    </div>
                                                )}
                                                <span
                                                    className={`text-sm ${
                                                        willBeRemoved
                                                            ? "text-gray-400 line-through"
                                                            : "text-gray-800"
                                                    }`}
                                                >
                                                    {p.name}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => togglePendingRemoval(p.id)}
                                                className="text-sm text-blue-600 hover:underline"
                                            >
                                                {willBeRemoved ? "Undo" : "Remove"}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div>
                        <div className="mb-2 font-semibold text-[#424242]">Add recipients</div>
                        <MultiUserSelector
                            value={usersToAdd}
                            users={addCandidates}
                            onChange={(ids) => setUsersToAdd((ids ?? []) as number[])}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleOpenChange(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleSave}
                            disabled={!hasChanges || isPending}
                        >
                            {isPending ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
                {externalOrgConfirmModal}
            </Dialog.Content>
        </Dialog.Root>
    );
};
